import { useRef, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { Instancia } from '../store';
import { instanciasAPI } from '../api';
import { useAuth } from '../hooks/useAuth';

export default function InstanceCommands({
  instance,
  onLogs,
  onCopy,
  onSupport,
  onCompleted,
}: {
  instance: Instancia;
  onLogs: () => void;
  onCopy: () => void;
  onSupport: () => void;
  onCompleted: () => void;
}) {
  const { can, showToast } = useAuth();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [confirmation, setConfirmation] = useState<'restart' | 'recreate' | null>(null);
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const select = (action: () => void) => {
    setAnchor(null);
    action();
  };
  const execute = async () => {
    if (!confirmation || pending.current) return;
    pending.current = true;
    setBusy(true);
    try {
      const response =
        confirmation === 'restart'
          ? await instanciasAPI.reiniciar(instance._id)
          : await instanciasAPI.recreate(instance._id);
      showToast(response.data.message, 'success');
      setConfirmation(null);
      onCompleted();
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Não foi possível executar o comando.', 'error');
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };
  return (
    <>
      <Button
        variant="outlined"
        size="small"
        endIcon={<KeyboardArrowDown />}
        aria-label={`Comandos de ${instance.nome}`}
        aria-haspopup="menu"
        aria-expanded={!!anchor}
        onClick={(event) => setAnchor(event.currentTarget)}
      >
        Comandos
      </Button>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { maxWidth: 'calc(100vw - 32px)', width: 340 } } }}
      >
        <MenuItem
          component="a"
          href={instance.frontend_url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setAnchor(null)}
        >
          Abrir aplicação
        </MenuItem>
        <MenuItem onClick={() => select(onCopy)}>Copiar alias</MenuItem>
        <MenuItem onClick={() => select(onLogs)}>Ver logs</MenuItem>
        {can('support.manage') && (
          <MenuItem disabled={instance.status !== 'ativo'} onClick={() => select(onSupport)}>
            Gerenciar meu acesso JBEADM
          </MenuItem>
        )}
        {(can('instances.restart') || can('infra.manage')) && <Divider />}
        {can('instances.restart') && (
          <MenuItem
            disabled={instance.status !== 'ativo'}
            onClick={() => select(() => setConfirmation('restart'))}
          >
            <ListItemText
              primary="Reiniciar aplicações…"
              secondary="Reinicia os serviços da instância"
              sx={{ whiteSpace: 'normal' }}
            />
          </MenuItem>
        )}
        {can('infra.manage') && (
          <MenuItem
            disabled={instance.status === 'criando' || instance.status === 'deletado'}
            onClick={() => select(() => setConfirmation('recreate'))}
          >
            <ListItemText
              primary="Reaplicar configurações…"
              secondary="Recria as aplicações com os dados do integrador"
              sx={{ whiteSpace: 'normal' }}
            />
          </MenuItem>
        )}
      </Menu>
      <Dialog
        open={!!confirmation}
        onClose={() => {
          if (!busy) setConfirmation(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {confirmation === 'restart' ? 'Confirmar reinício' : 'Confirmar reaplicação de configurações'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Instância: <strong>{instance.nome}</strong>
          </Typography>
          <Alert severity="warning">
            {confirmation === 'restart'
              ? 'Os serviços serão reiniciados e o acesso pode ficar temporariamente indisponível.'
              : 'As aplicações serão recriadas com as configurações atuais do integrador e a imagem publicada. Pode haver indisponibilidade durante o processo. O banco de dados será preservado.'}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setConfirmation(null)}>
            Cancelar
          </Button>
          <Button color="warning" variant="contained" disabled={busy} onClick={() => void execute()}>
            {busy
              ? 'Executando…'
              : confirmation === 'restart'
              ? 'Confirmar reinício'
              : 'Confirmar reaplicação'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
